import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

/**
 * Setup workflow sequence — the order beta testers should populate data so
 * the Dashboard feels populated the first time they reach it.
 *
 * Per beta feedback (Feb 2026): Profile → Income → Expenses → Assets &
 * Liabilities (single Net Worth page) → Dashboard. After Dashboard, the user
 * has the full data they need to explore the rest of the app.
 */
export const SETUP_STEPS = [
  { path: '/financial-profile', label: 'Personal Profile', short: 'Profile' },
  { path: '/income-sources', label: 'Income Sources', short: 'Income' },
  { path: '/spending-tracker', label: 'Monthly Expenses', short: 'Expenses' },
  { path: '/net-worth', label: 'Assets & Liabilities', short: 'Net Worth' },
  { path: '/', label: 'Dashboard', short: 'Dashboard' },
];

/**
 * Bottom-of-page navigation: Previous Step / Save & Continue.
 *
 * `onContinue` is optional. If provided, it should return either truthy/void to
 * advance, or `false` to block the navigation (e.g. saving failed). If you
 * don't need a save action, leave it out and the button just routes to the
 * next step.
 */
const WorkflowStepper = ({ onContinue, continueLabel, testid = 'workflow-stepper' }) => {
  const location = useLocation();
  const currentIdx = SETUP_STEPS.findIndex((s) => s.path === location.pathname);
  if (currentIdx === -1) return null; // page not in the setup flow

  const prev = currentIdx > 0 ? SETUP_STEPS[currentIdx - 1] : null;
  const next = currentIdx < SETUP_STEPS.length - 1 ? SETUP_STEPS[currentIdx + 1] : null;
  const isFinal = !next;

  // Compute progress %
  const progressPct = Math.round(((currentIdx + 1) / SETUP_STEPS.length) * 100);

  const handleContinue = async (e) => {
    if (!onContinue || !next) return;
    e.preventDefault();
    try {
      const result = await onContinue();
      if (result === false) return; // saver indicated failure
    } catch {
      return;
    }
    window.location.assign(next.path);
  };

  return (
    <div
      data-testid={testid}
      className="mt-10 border-t border-slate-200 pt-6 pb-2 space-y-4"
    >
      {/* Progress strip — horizontally scrollable on mobile, full flex row on sm+ */}
      <div className="-mx-1 overflow-x-auto sm:mx-0 sm:overflow-visible">
        <div
          className="flex items-center gap-2 min-w-max sm:min-w-0 px-1 sm:px-0"
          aria-label={`Setup step ${currentIdx + 1} of ${SETUP_STEPS.length}`}
        >
          {SETUP_STEPS.map((s, i) => {
            const done = i < currentIdx;
            const current = i === currentIdx;
            return (
              <div key={s.path} className="flex items-center gap-2 sm:flex-1">
                <div className="flex items-center gap-1.5 sm:flex-1 min-w-0">
                  <div
                    className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                      done
                        ? 'bg-[#2D6A4F] text-white'
                        : current
                          ? 'bg-[#C5A059] text-[#0A1128]'
                          : 'bg-slate-200 text-[#4A5568]'
                    }`}
                  >
                    {done ? <CheckCircle size={12} weight="fill" /> : i + 1}
                  </div>
                  <span
                    className={`text-xs whitespace-nowrap sm:truncate ${current ? 'text-[#0A1128] font-semibold' : done ? 'text-[#2D6A4F]' : 'text-[#A0AABF]'}`}
                    data-testid={`${testid}-step-${i}`}
                  >
                    {s.short}
                  </span>
                </div>
                {i < SETUP_STEPS.length - 1 && (
                  <div className={`h-px flex-shrink-0 w-4 ${done ? 'bg-[#2D6A4F]' : 'bg-slate-200'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-[#4A5568] text-center">
        Setup progress: <span className="font-semibold text-[#0A1128]">{progressPct}%</span> &nbsp;·&nbsp; Step {currentIdx + 1} of {SETUP_STEPS.length}
      </p>

      {/* Prev / Next buttons — stack on mobile, side-by-side on sm+ */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        {prev ? (
          <Link to={prev.path} data-testid={`${testid}-prev`} className="w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto border-slate-300 text-[#0A1128] hover:bg-slate-50 rounded-md flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} /> Previous: {prev.short}
            </Button>
          </Link>
        ) : <div className="hidden sm:block" />}

        {next ? (
          onContinue ? (
            <Button
              type="button"
              onClick={handleContinue}
              data-testid={`${testid}-continue`}
              className="w-full sm:w-auto bg-[#0A1128] text-white hover:bg-[#152145] rounded-md flex items-center justify-center gap-2"
            >
              {continueLabel || `Save & Continue to ${next.short}`} <ArrowRight size={16} />
            </Button>
          ) : (
            <Link to={next.path} data-testid={`${testid}-continue`} className="w-full sm:w-auto">
              <Button
                type="button"
                className="w-full sm:w-auto bg-[#0A1128] text-white hover:bg-[#152145] rounded-md flex items-center justify-center gap-2"
              >
                Continue to {next.short} <ArrowRight size={16} />
              </Button>
            </Link>
          )
        ) : (
          <span data-testid={`${testid}-complete`} className="text-xs text-[#2D6A4F] font-semibold uppercase tracking-[0.08em]">Setup complete 🎉</span>
        )}
      </div>
    </div>
  );
};

export default WorkflowStepper;
